import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Button, Alert, Spinner, Collapse } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import AnfrageModal from '../components/AnfrageModal';
import './TutorProfile.scss';

moment.locale('de');
const localizer = momentLocalizer(moment);

const WOC_MAP = { 'Mo': 1, 'Di': 2, 'Mi': 3, 'Do': 4, 'Fr': 5, 'Sa': 6, 'So': 7 };

const MOCK_SKILL_RATINGS = {
  'Mathematik': 5.0,
  'Physik': 1.0,
  'Java': 4.5,
  'Python': 4.0,
  'Lineare Algebra': 3.0,
  'Datenbanken': 4.0
};

const getMockRatingForSkill = (skillName) => {
  if (MOCK_SKILL_RATINGS.hasOwnProperty(skillName)) {
    return MOCK_SKILL_RATINGS[skillName];
  }
  let hash = 0;
  for (let i = 0; i < skillName.length; i++) {
    hash = skillName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const rating = 3.0 + (Math.abs(hash) % 21) / 10.0;
  return Math.round(rating * 2) / 2;
};

const Toolbar = (t) => {
  const lbl = () => <span className="rbc-toolbar-label">{moment(t.date).format('MMMM YYYY')}</span>;
  return (
    <div className="rbc-toolbar unified-toolbar">
      <div className="rbc-btn-group">
        <button type="button" onClick={() => t.onNavigate('PREV')}><i className="bi bi-chevron-left"></i></button>
        <button type="button" onClick={() => t.onNavigate('TODAY')}><i className="bi bi-calendar-event"></i></button>
        <button type="button" onClick={() => t.onNavigate('NEXT')}><i className="bi bi-chevron-right"></i></button>
      </div>
      <div className="rbc-toolbar-label-wrap">{lbl()}</div>
      <div className="rbc-btn-group">
        {['month', 'work_week', 'day'].map(v => <button key={v} type="button" className={t.view === v ? 'rbc-active' : ''} onClick={() => t.onView(v)}>{v === 'month' ? 'Monat' : v === 'day' ? 'Tag' : 'Woche'}</button>)}
      </div>
    </div>
  );
};

export default function TutorProfile() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const { token, user } = useAuth();
  const [tutor, setTutor] = useState(null);
  const [avail, setAvail] = useState([]);
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selEvent, setSelEvent] = useState(null);
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState(true);
  
  const [reviews, setReviews] = useState([]);
  const [overallAvg, setOverallAvg] = useState(0);
  const [lessonsMap, setLessonsMap] = useState({});

  const calculatedAvg = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((a, b) => a + b.bewertung, 0);
    return Math.round((sum / reviews.length) * 10) / 10;
  }, [reviews]);

  const displayAverage = useMemo(() => {
    if (typeof overallAvg === 'number' && !isNaN(overallAvg) && overallAvg > 0) {
      return Math.round(overallAvg * 10) / 10;
    }
    return calculatedAvg;
  }, [overallAvg, calculatedAvg]);

  const ratingSummary = useMemo(() => {
    if (!tutor?.skills || tutor.skills.length === 0) return null;
    
    // Group ratings by skill name
    const ratingsBySkill = {};
    tutor.skills.forEach(s => {
      ratingsBySkill[s.name] = [];
    });

    reviews.forEach(rev => {
      let skillName = lessonsMap[rev.lernEinheitId];
      if (!skillName && tutor.skills.length === 1) {
        skillName = tutor.skills[0].name;
      }
      if (skillName && ratingsBySkill.hasOwnProperty(skillName)) {
        ratingsBySkill[skillName].push(rev.bewertung);
      }
    });

    const skillsWithRatings = tutor.skills.map(s => {
      const skillRatings = ratingsBySkill[s.name] || [];
      const rating = skillRatings.length > 0
        ? Math.round((skillRatings.reduce((a, b) => a + b, 0) / skillRatings.length) * 2) / 2
        : undefined;
      return { ...s, rating };
    });
    
    return {
      average: displayAverage,
      skills: skillsWithRatings,
      count: reviews.length
    };
  }, [tutor?.skills, reviews, lessonsMap, displayAverage]);

  const [view, setView] = useState(window.innerWidth < 992 ? 'day' : 'work_week');

  useEffect(() => {
    const res = () => setView(window.innerWidth < 992 ? 'day' : 'work_week');
    window.addEventListener('resize', res);
    return () => window.removeEventListener('resize', res);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let cur = loc.state?.tutor;
        if (!cur) {
          const r = await fetch(`${getBackendUrl()}/api/tutors/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (!r.ok) throw new Error('Nicht gefunden.');
          cur = await r.json();
        }
        
        // Fetch all dependencies in parallel
        const [aR, bR, sR, revsR, avgR, lessR] = await Promise.all([
          fetch(`${getBackendUrl()}/api/tutors/${id}/availability`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${getBackendUrl()}/api/tutors/${id}/ausgebuchte-zeiten`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${getBackendUrl()}/skill`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${getBackendUrl()}/api/bewertungen/tutor/${id}`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${getBackendUrl()}/api/bewertungen/tutor/${id}/avg`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${getBackendUrl()}/termine`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        if (!aR.ok || !bR.ok || !sR.ok) {
          throw new Error('Fehler beim Laden der Profildaten.');
        }

        const [aD, bD, sD] = await Promise.all([aR.json(), bR.json(), sR.json()]);
        
        let revsData = [];
        if (revsR.ok) {
          revsData = await revsR.json();
        }
        setReviews(revsData);

        if (avgR.ok) {
          const avgVal = await avgR.json();
          setOverallAvg(isNaN(Number(avgVal)) ? 0 : Number(avgVal));
        }

        if (lessR.ok) {
          const lessData = await lessR.json();
          const map = {};
          lessData.forEach(l => {
            if (l.id && l.skillName) {
              map[l.id] = l.skillName;
            }
          });
          setLessonsMap(map);
        }

        setAvail(aD); setBooked(bD);
        setTutor({ ...cur, skills: (cur.skills || []).map(ts => ({ ...ts, id: sD.find(s => s.name === (ts.name || ts.skillName))?.id, name: ts.name || ts.skillName })) });
      } catch (e) { 
        console.error("Fehler im Dozentenprofil:", e);
        setError(e.message); 
      } finally { 
        setLoading(false); 
      }
    })();
  }, [id, token, loc.state]);

  const events = useMemo(() => {
    const ev = [];
    const today = moment().startOf('day');
    const bks = booked.map(s => ({ s: moment(s.start).valueOf(), e: moment(s.ende).valueOf() }));
    avail.forEach(av => {
      const iso = WOC_MAP[av.wochentag];
      if (iso) {
        for (let w = 0; w < 4; w++) {
          const s = moment().startOf('isoWeek').add(w, 'weeks').isoWeekday(iso).hours(parseInt(av.startZeit.split(':')[0])).minutes(parseInt(av.startZeit.split(':')[1])).seconds(0);
          const e = moment().startOf('isoWeek').add(w, 'weeks').isoWeekday(iso).hours(parseInt(av.endeZeit.split(':')[0])).minutes(parseInt(av.endeZeit.split(':')[1])).seconds(0);
          if (!s.isBefore(today)) {
            let ints = [{ s: s.valueOf(), e: e.valueOf() }];
            bks.forEach(b => {
              let next = [];
              ints.forEach(i => {
                if (b.s < i.e && b.e > i.s) {
                  if (i.s < b.s) next.push({ s: i.s, e: b.s });
                  if (i.e > b.e) next.push({ s: b.e, e: i.e });
                } else next.push(i);
              });
              ints = next;
            });
            ints.forEach((i, idx) => ev.push({ id: `a-${av.id}-${w}-${idx}`, title: 'Verfügbar', start: new Date(i.s), end: new Date(i.e), type: 'available' }));
          }
        }
      }
    });
    booked.forEach(s => ev.push({ id: `b-${s.id}`, title: 'Ausgebucht', start: new Date(s.start), end: new Date(s.ende), type: 'booked' }));
    return ev;
  }, [avail, booked]);

  const submit = async (d) => {
    const r = await fetch(`${getBackendUrl()}/api/anfragen`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(d) });
    if (!r.ok) throw new Error('Fehler.');
    setSuccess('Erfolgreich versendet!');
    const bR = await fetch(`${getBackendUrl()}/api/tutors/${id}/ausgebuchte-zeiten`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (bR.ok) setBooked(await bR.json());
  };

  if (error) {
    return (
      <Container className="vintage-tutor-profile py-4">
        <Button variant="link" onClick={() => nav(-1)} className="mb-3 text-decoration-none" style={{ color: '#b95c50', fontWeight: 600 }}>&larr; Zurück</Button>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!user || !tutor) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <Container className="vintage-tutor-profile py-4">
      <Button variant="link" onClick={() => nav(-1)} className="mb-3 text-decoration-none" style={{ color: '#b95c50', fontWeight: 600 }}>&larr; Zurück</Button>
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      <Card className="profile-card mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="profile-name mb-1">{tutor.vollerName}</h1>
              <p className="text-muted mb-2">{tutor.email}</p>
              
              {reviews.length > 0 ? (
                <div className="profile-rating-summary d-flex align-items-center mb-3">
                  <div className="stars me-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFull = star <= Math.floor(displayAverage);
                      const isHalf = !isFull && (star - 0.5 <= displayAverage);
                      return (
                        <i 
                          key={star} 
                          className={`bi ${isFull ? 'bi-star-fill' : isHalf ? 'bi-star-half' : 'bi-star'} text-warning`} 
                          style={{ fontSize: '1.25rem', marginRight: '3px' }}
                        />
                      );
                    })}
                  </div>
                  <span className="rating-avg fw-bold text-dark me-2">{displayAverage.toFixed(1)}</span>
                  <span className="rating-count text-muted">({reviews.length} {reviews.length === 1 ? 'Bewertung' : 'Bewertungen'})</span>
                </div>
              ) : (
                <div className="profile-rating-summary d-flex align-items-center mb-3">
                  <span className="rating-count text-muted">Noch keine Bewertungen</span>
                </div>
              )}
              
              <p className="profile-bio">{tutor.bio || 'Keine Bio.'}</p>
            </div>
            {tutor.id !== user?.id && (
              <Button className="chat-button" onClick={() => nav(`/chat/${id}`)}>
                Chat eröffnen
              </Button>
            )}
          </div>
          
          {tutor.skills && tutor.skills.length > 0 && (
            <div className="mt-3 pt-3 border-top skills-collapsible-section">
              <div 
                className="d-flex justify-content-between align-items-center mb-0" 
                onClick={() => setExpanded(!expanded)} 
                style={{ cursor: 'pointer' }}
              >
                <h5 className="mb-0">Qualifikationen & Angebote</h5>
                <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} text-muted`}></i>
              </div>
              <Collapse in={expanded}>
                <div className="pt-3">
                  <div className="skills-grid">
                    {(ratingSummary?.skills || tutor.skills)?.filter(s => s.name && !s.name.includes('-deleted')).map(s => (
                      <div key={s.id || s.name} className="skill-item">
                        <div className="skill-info">
                          <span className="skill-name">{s.name}</span>
                          <span className="skill-meta">{s.niveau}</span>
                          {s.rating !== undefined ? (
                            <div className="skill-rating mt-1 d-flex align-items-center">
                              <div className="stars-mini me-1">
                                {[1, 2, 3, 4, 5].map((star) => {
                                  const isFull = star <= Math.floor(s.rating);
                                  const isHalf = !isFull && (star - 0.5 <= s.rating);
                                  return (
                                    <i 
                                      key={star} 
                                      className={`bi ${isFull ? 'bi-star-fill' : isHalf ? 'bi-star-half' : 'bi-star'} text-warning`} 
                                      style={{ fontSize: '0.8rem', marginRight: '1px' }}
                                    />
                                  );
                                })}
                              </div>
                              <span className="small text-muted">({s.rating.toFixed(1)})</span>
                            </div>
                          ) : (
                            <div className="skill-rating mt-1">
                              <span className="small text-muted">Noch keine Bewertungen</span>
                            </div>
                          )}
                        </div>
                        <div className="skill-price-wrap">{s.stundenlohn} €/h</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Collapse>
            </div>
          )}
        </Card.Body>
      </Card>
      
      <Card className="booking-card border-0 shadow-sm">
        <Card.Header className="bg-white">
          <h3>{user?.role === 'dozent' ? 'Verfügbarkeiten' : 'Termin anfragen'}</h3>
        </Card.Header>
        <Card.Body className="p-0 p-md-3">
          <div className="calendar-wrapper" style={{ height: '600px' }}>
            <Calendar 
              localizer={localizer} 
              events={events} 
              view={view} 
              onView={v => setView(v)} 
              views={['work_week', 'month', 'day']}
              min={new Date(0, 0, 0, 8, 0, 0)}
              max={new Date(0, 0, 0, 20, 0, 0)}
              onSelectEvent={e => e.type === 'available' && tutor.id !== user?.id && (setSelEvent(e), setShowModal(true))} 
              eventPropGetter={e => ({ style: { backgroundColor: e.type === 'available' ? '#b95c50' : '#8c8c8c', color: '#fff', borderRadius: '6px', border: 'none' } })} 
              components={{ toolbar: Toolbar }} 
              messages={{ today: 'Heute', previous: 'Zurück', next: 'Weiter', month: 'Monat', week: 'Woche', day: 'Tag' }} 
            />
          </div>
        </Card.Body>
      </Card>
      <Card className="reviews-card border-0 shadow-sm mt-4">
        <Card.Header className="bg-white">
          <h3 className="mb-0">Kundenstimmen & Feedback</h3>
        </Card.Header>
        <Card.Body className="p-4">
          {reviews.length === 0 ? (
            <p className="text-muted mb-0">Noch keine schriftlichen Bewertungen für diesen Dozenten vorhanden.</p>
          ) : (
            <div className="reviews-list">
              {reviews.map((rev) => (
                <div key={rev.id} className="review-item mb-3 pb-3 border-bottom">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div className="stars-mini">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i 
                            key={star} 
                            className={`bi ${star <= rev.bewertung ? 'bi-star-fill' : 'bi-star'} text-warning`} 
                            style={{ fontSize: '0.9rem', marginRight: '2px' }}
                          />
                        ))}
                      </div>
                      <span className="badge bg-light text-dark border small">
                        {lessonsMap[rev.lernEinheitId] || (tutor.skills.length === 1 ? tutor.skills[0].name : 'Allgemein')}
                      </span>
                    </div>
                    <small className="text-muted">
                      {rev.erstelltAm ? moment(rev.erstelltAm).format('DD. MMMM YYYY') : ''}
                    </small>
                  </div>
                  {rev.kommentar ? (
                    <p className="review-comment mb-0 text-dark" style={{ fontStyle: 'italic' }}>
                      "{rev.kommentar}"
                    </p>
                  ) : (
                    <p className="review-comment mb-0 text-muted small">
                      Kein schriftlicher Kommentar hinterlassen.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      
      {selEvent && <AnfrageModal show={showModal} onHide={() => setShowModal(false)} event={selEvent} tutor={tutor} onSubmit={submit} />}
    </Container>
  );
}
